import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance, { isAxiosError } from "@/utils/axiosInstance";
import { Params } from "@/types";
import { handleThunkError } from "@/utils/thunkErrorHandler";

const endpoint = {
  base: "/subscriptions",
};

const withEstablishmentPrefix = (path: string, establishmentSlug?: string) => {
  if (!establishmentSlug) return path;

  const baseURL = axiosInstance.defaults.baseURL || "";
  const alreadyScoped =
    path.startsWith("/establishment/") || baseURL.includes("/establishment/");

  return alreadyScoped ? path : `/establishment/${establishmentSlug}${path}`;
};

export const fetchClientSubscriptions = createAsyncThunk(
  "clientSubscriptions/fetchClientSubscriptions",
  async (
    {
      establishmentSlug,
      params,
    }: { establishmentSlug?: string; params: Params },
    thunkAPI,
  ) => {
    try {
      const path = withEstablishmentPrefix(
        `${endpoint.base}/subscribers`,
        establishmentSlug,
      );

      // Serialize status filter supporting comma or array syntax
      const searchParams = new URLSearchParams();
      Object.entries(params || {}).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (key === "status") {
          if (Array.isArray(value)) {
            // Prefer array syntax: filter[status][]
            value.forEach((v) =>
              searchParams.append("filter[status][]", String(v)),
            );
          } else {
            searchParams.set("status", String(value));
          }
        } else if (key === "filter" && typeof value === "object") {
          Object.entries(value as Record<string, any>).forEach(
            ([fKey, fVal]) => {
              if (fVal === undefined || fVal === null) return;
              searchParams.set(`filter[${fKey}]`, String(fVal));
            },
          );
        } else {
          searchParams.set(key, String(value));
        }
      });

      const query = searchParams.toString();
      const url = query ? `${path}?${query}` : path;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao buscar assinaturas de cliente",
        });
      }
    }
  },
);

export const fetchClientSubscriptionDetails = createAsyncThunk(
  "clientSubscriptions/fetchClientSubscriptionDetails",
  async (
    {
      subscriptionId,
      clientSubscriptionId,
      establishmentSlug,
    }: {
      subscriptionId: string | number;
      clientSubscriptionId: string | number;
      establishmentSlug?: string;
    },
    thunkAPI,
  ) => {
    try {
      const path = `${endpoint.base}/${subscriptionId}/clients/${clientSubscriptionId}`;
      const url = withEstablishmentPrefix(path, establishmentSlug);

      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao buscar detalhes da assinatura",
        });
      }
    }
  },
);

export const refundAsaasPayment = createAsyncThunk(
  "clientSubscriptions/refundAsaasPayment",
  async (
    {
      establishmentSlug,
      asaasPaymentId,
    }: { establishmentSlug?: string; asaasPaymentId: string },
    thunkAPI,
  ) => {
    try {
      // `axiosInstance` already includes the establishment slug in its baseURL/session.
      // Use the payments endpoint directly (no `/subscriptions` segment).
      const path = `/payments/${asaasPaymentId}/refund-asaas`;
      const response = await axiosInstance.post(path);
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const body: any = (error.response && error.response.data) || null;
        return thunkAPI.rejectWithValue({
          error: body?.message || error.message || "Erro ao solicitar estorno",
        });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao solicitar estorno",
        });
      }
    }
  },
);

export const createClientSubscription = createAsyncThunk(
  "clientSubscriptions/createClientSubscription",
  async ({ data }: { data: any }, thunkAPI) => {
    try {
      // Usar o endpoint padrão para criação de cliente em assinatura.
      // Exigir subscription_id: não deixar o fallback para `/subscriptions/clients` (gera 405 quando usado com POST).
      if (!data || !data.subscription_id) {
        return thunkAPI.rejectWithValue({
          error: "Missing subscription_id for createClientSubscription",
        });
      }
      const url = `${endpoint.base}/${data.subscription_id}/clients`;
      const response = await axiosInstance.post(url, data);
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorMessage = error?.response?.data as any;
        return thunkAPI.rejectWithValue({ error: errorMessage });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro ao criar assinatura do cliente",
        });
      }
    }
  },
);

export const updateClientSubscription = createAsyncThunk(
  "clientSubscriptions/updateClientSubscription",
  async (
    {
      subscription_id,
      slug,
      data,
    }: { subscription_id: string | number; slug: number; data: any },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.put(
        `${endpoint.base}/${subscription_id}/clients/${slug}/change-plan`,
        data,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao atualizar cliente da assinatura",
        });
      }
    }
  },
);

export const deleteClientSubscription = createAsyncThunk(
  "clientSubscriptions/deleteClientSubscription",
  async (
    { subscription_id, slug }: { subscription_id: string; slug: string },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.patch(
        `${endpoint.base}/${subscription_id}/clients/${slug}/cancel`,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao excluir cliente da assinatura",
        });
      }
    }
  },
);

export const pauseClientSubscription = createAsyncThunk(
  "clientSubscriptions/pauseClientSubscription",
  async (
    { subscription_id, slug }: { subscription_id: string; slug: string },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.patch(
        `${endpoint.base}/${subscription_id}/clients/${slug}/pause`,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao pausar cliente da assinatura",
        });
      }
    }
  },
);

export const activateClientSubscription = createAsyncThunk(
  "clientSubscriptions/activateClientSubscription",
  async (
    { subscription_id, slug }: { subscription_id: string; slug: string },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.patch(
        `${endpoint.base}/${subscription_id}/clients/${slug}/activate`,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao ativar cliente da assinatura",
        });
      }
    }
  },
);

export const deleteClientSubscriptionViaPut = createAsyncThunk<
  any,
  { subscription_id: string; slug: string }
>(
  "clientSubscriptions/deleteClientSubscriptionViaPut",
  async (
    { subscription_id, slug }: { subscription_id: string; slug: string },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.put(
        `${endpoint.base}/${subscription_id}/clients/${slug}/delete`,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const body: any = (error.response && error.response.data) || null;
        const message =
          body?.message || body?.reason || body?.error || error.message;
        return thunkAPI.rejectWithValue({ error: message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao excluir cliente da assinatura",
        });
      }
    }
  },
);

export const markClientSubscriptionExpired = createAsyncThunk<
  any,
  { subscription_id: string; slug: string }
>(
  "clientSubscriptions/markClientSubscriptionExpired",
  async (
    { subscription_id, slug }: { subscription_id: string; slug: string },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.put(
        `${endpoint.base}/${subscription_id}/clients/${slug}/expired`,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const body: any = (error.response && error.response.data) || null;
        const message =
          body?.message || body?.reason || body?.error || error.message;
        return thunkAPI.rejectWithValue({ error: message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao marcar assinatura como vencida",
        });
      }
    }
  },
);

export const destroyClientSubscription = createAsyncThunk(
  "clientSubscriptions/destroyClientSubscription",
  async (
    { subscription_id, slug }: { subscription_id: string; slug: string },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.delete(
        `${endpoint.base}/${subscription_id}/clients/${slug}`,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const body: any = (error.response && error.response.data) || null;
        const message =
          body?.message ||
          error.message ||
          "Erro desconhecido ao excluir cliente da assinatura";
        return thunkAPI.rejectWithValue({ error: message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao excluir cliente da assinatura",
        });
      }
    }
  },
);

export const generatePaymentLink = createAsyncThunk(
  "clientSubscriptions/generatePaymentLink",
  async (
    {
      subscription_id,
      client_sub_id,
      payment_method,
      customer,
    }: {
      subscription_id: string;
      client_sub_id: number;
      payment_method: string;
      customer: any;
    },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.post(
        `${endpoint.base}/${subscription_id}/clients/subscriptions`,
        {
          subscription_id: subscription_id,
          type: payment_method,
          client_id: client_sub_id,
          customer,
        },
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorMessage = error?.response?.data as any;
        return thunkAPI.rejectWithValue({
          error: errorMessage?.error || errorMessage?.message || error.message,
        });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao criar agendamento.",
        });
      }
    }
  },
);

export const tryChargeSubscription = createAsyncThunk(
  "clientSubscriptions/tryChargeSubscription",
  async (
    {
      subscription_id,
      client_sub_id,
    }: { subscription_id: string; client_sub_id: number },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.post(
        `${endpoint.base}/${subscription_id}/clients/${client_sub_id}/payments/try-charge`,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorMessage = error?.response?.data as any;
        return thunkAPI.rejectWithValue({
          error: errorMessage?.error || errorMessage?.message || error.message,
        });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao tentar cobrar",
        });
      }
    }
  },
);

export const tryChargeSubscriptionWithCard = createAsyncThunk(
  "clientSubscriptions/tryChargeSubscriptionWithCard",
  async (
    {
      subscription_id,
      client_sub_id,
      payload,
    }: {
      subscription_id: string;
      client_sub_id: number;
      payload: {
        card_token_id: number;
        payment_type: string;
        customer: any;
      };
    },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.post(
        `${endpoint.base}/${subscription_id}/clients/${client_sub_id}/payments/try-charge`,
        payload,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorMessage = error?.response?.data as any;
        return thunkAPI.rejectWithValue({
          error: errorMessage?.error || errorMessage?.message || error.message,
        });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao tentar cobrar com cartão",
        });
      }
    }
  },
);

export const fetchClientSubscriptionUsage = createAsyncThunk(
  "clientSubscriptions/fetchClientSubscriptionUsage",
  async (
    {
      subscriptionId,
      clientSubscriptionId,
    }: {
      subscriptionId: string | number;
      clientSubscriptionId: string | number;
    },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.get(
        `${endpoint.base}/${subscriptionId}/clients/${clientSubscriptionId}/usage`,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao buscar histórico de utilização",
        });
      }
    }
  },
);

// Corrigido para buscar os dados reais da API usando o slug do estabelecimento
export const fetchSubscribersStats = createAsyncThunk(
  "clientSubscriptions/fetchSubscribersStats",
  async (
    {
      establishmentSlug,
      params,
    }: {
      establishmentSlug: string;
      params?: { date_from?: string; date_to?: string };
    },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.get(`/subscriptions/stats`, {
        params,
      });
      //console.log('Subscribers Stats:', response);
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao buscar estatísticas de assinantes",
        });
      }
    }
  },
);
